import { LineItemData } from 'interfaces/line-item'
import React, { useState } from 'react'
import { Form, Alert, Button, Card } from 'react-bootstrap'
import { FuzzySearchModal } from './fuzzy-search-modal'

export const StripeSelect = ({
  setState,
  id,
  value,
  data = [],
  nullValue,
  label,
  labelDescription,
  showRefetchButton = false,
  refetch = () => {},
  isFetching,
  includeSearch = true,
}: {
  setState: React.Dispatch<React.SetStateAction<string | number | undefined>>
  id: string
  value?: string | number
  data?: LineItemData[]
  nullValue: string
  label: string
  labelDescription: string
  showRefetchButton?: boolean
  refetch?: () => void
  isFetching: boolean
  includeSearch?: boolean
}) => {
  const [showModal, setShowModal] = useState(false)

  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setState(e.target.value === 'None' ? '' : e.target.value)
  }

  const itemName = (item?: LineItemData | null | undefined) => {
    if (!item) return ''
    if (id === 'coupons') {
      return `${item.name} - ${
        item.amount_off
          ? ` ${Number(item.amount_off / 100).toFixed(2)} `
          : ` ${item.percent_off}% `
      } ${
        item.duration === 'repeating'
          ? `for ${item.duration_in_months} month`
          : item.duration
      }`
    }
    if (id === 'productPrice') {
      return `${
        item?.metadata?.internal_description
          ? item?.metadata?.internal_description
          : `$ ${(Number(item?.unit_amount) / 100).toFixed(2)}`
      }`
    }
    return item?.name
  }

  const findItem = (id?: number | string) =>
    id ? data.find((item) => item.id === id) : null

  const validData =
    data
      ?.filter((item) => {
        if (id === 'coupons') {
          return item.valid
        }
        if (id === 'productPrice' || id === 'products') {
          return item.active
        }
        return true
      })
      .map((item) => ({ ...item, name: itemName(item) })) || []

  const onCloseModal = () => setShowModal(false)

  const handleSelectClick = (item: LineItemData) => {
    setState(item?.id || '')
    setShowModal(false)
  }

  const handleNewCouponClick = () => {
    window.open('https://dashboard.stripe.com/coupons', '_blank')
  }

  return (
    <>
      <div className="mt-2 mb-2">
        <div className="form-floating" style={{ display: 'flex' }}>
          <select
            onChange={handleOnChange}
            className="form-select"
            id={id}
            value={value}
            disabled={isFetching}
          >
            <option value="None">{nullValue}</option>
            {validData?.map((item) => (
              <option key={item.id} value={item.id}>
                {itemName(item)}
              </option>
            ))}
          </select>
          <label htmlFor={label}>{labelDescription}</label>
          {includeSearch && (
            <Button
              style={{ marginLeft: '8px' }}
              onClick={() => setShowModal(true)}
            >
              <ion-icon name="search" />
            </Button>
          )}
        </div>
        {id === 'coupons' && (
          <Button
            variant="outline-light"
            className="mt-2 ms-2"
            onClick={handleNewCouponClick}
          >
            Stripe Coupons <ion-icon name="open-outline" />
          </Button>
        )}
      </div>
      <FuzzySearchModal
        show={showModal}
        onClose={onCloseModal}
        initialQuery={itemName(findItem(value))}
        itemName={itemName}
        handleSelectClick={handleSelectClick}
        selectButtonText="Select"
        data={validData}
        modalTitle={labelDescription || label}
      />
    </>
  )
}

export const AdjustProductQuantity = ({
  productQuantity,
  setProductQuantity,
  setAllowQuantityAdjusting,
  allowQuantityAdjusting,
}: {
  productQuantity: number
  setProductQuantity: React.Dispatch<React.SetStateAction<number>>
  setAllowQuantityAdjusting: React.Dispatch<React.SetStateAction<boolean>>
  allowQuantityAdjusting: boolean
}) => {
  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedQuantity = Number(e.target.value)
    if (updatedQuantity < 0) {
      window.alert('Quantity can not be less than 0')
      return
    }
    setProductQuantity(updatedQuantity)
    if (updatedQuantity >= 999) {
      setAllowQuantityAdjusting(false)
    } else if (updatedQuantity < 999 && allowQuantityAdjusting) {
      setAllowQuantityAdjusting(true)
    } else if (updatedQuantity < 999 && !allowQuantityAdjusting) {
      setAllowQuantityAdjusting(false)
    }
  }
  return (
    <div className="form-floating mt-2 mb-2">
      <Form.Group className="mb-3">
        <Form.Label
          style={{
            background: '#fff',
            width: '100%',
            marginBottom: '-0.5rem',
            padding: '0.5rem',
            color: 'gray',
            borderRadius: '0.25rem',
          }}
        >
          Quantity
        </Form.Label>
        <Form.Control
          style={{ borderColor: '#fff' }}
          type="number"
          value={productQuantity}
          onChange={handleChangeQuantity}
          placeholder="0"
        />
      </Form.Group>
    </div>
  )
}

export const AdjustSeatCountCheckBox = ({
  productQuantity,
  allowQuantityAdjusting,
  handleAllowQuantityAdjusting,
}: {
  productQuantity: number
  allowQuantityAdjusting: boolean
  handleAllowQuantityAdjusting: (value: boolean) => void
}) => {
  return (
    <div className="form-check mt-2">
      <input
        key={Math.random()}
        className="form-check-input"
        type="checkbox"
        disabled={productQuantity > 999 || productQuantity < 1}
        checked={allowQuantityAdjusting}
        onChange={(e) => handleAllowQuantityAdjusting(e.target.checked)}
        id="adjustableSeatCountCheck"
      />
      <label className="form-check-label" htmlFor="adjustableSeatCountCheck">
        Let User Adjust Product Quantity?
      </label>
    </div>
  )
}

export const AdjustSeatCountWarning = ({
  productQuantity,
  allowQuantityAdjusting,
}: {
  productQuantity: number
  allowQuantityAdjusting: boolean
}) => {
  return (
    <>
      {productQuantity > 999 && !allowQuantityAdjusting ? (
        <Alert variant="secondary" className="mt-2">
          Can't have adjustable quantity enabled when the quantity is over 999.
        </Alert>
      ) : productQuantity < 5 && !allowQuantityAdjusting ? (
        <Alert variant="secondary" className="mt-2">
          Can't have adjustable quantity enabled when the quantity is less than
          1
        </Alert>
      ) : (
        <Alert variant="secondary" className="mt-2">
          Quantity can only be adjusted from 1 to 999 and at checkout by the
          user.
        </Alert>
      )}
    </>
  )
}

export const LinqOneDiscountInput = ({
  linqOneDiscount,
  setLinqOneDiscount,
}: {
  linqOneDiscount: string | number | undefined
  setLinqOneDiscount: (value: string | number | undefined) => void
}) => {
  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percent = Number(e.target.value)

    if (Number.isNaN(percent) || percent < 0 || percent > 100) {
      alert('Invalid discount! Please enter a value between 0 and 100.')
      return false
    }

    setLinqOneDiscount(percent)
  }
  return (
    <div className="form-floating mt-2 mb-2">
      <Form.Group className="mb-3">
        <Form.Label
          style={{
            background: '#fff',
            width: '100%',
            marginBottom: '-0.5rem',
            padding: '0.5rem',
            color: 'gray',
            borderRadius: '0.25rem',
          }}
        >
          Linq One Discount
        </Form.Label>
        <Form.Control
          style={{ borderColor: '#fff' }}
          type="number"
          value={linqOneDiscount}
          onChange={handleChangeQuantity}
          placeholder="0"
        />
      </Form.Group>
    </div>
  )
}

export const LinqCouponId = ({
  couponId,
  setCouponId,
}: {
  couponId: string | undefined
  setCouponId: (value: string | undefined) => void
}) => {
  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const coupon = e.target.value.trim()
    setCouponId(coupon)
  }
  return (
    <div className="form-floating">
      <Form.Group>
        <Form.Label
          style={{
            background: '#fff',
            width: '100%',
            marginBottom: '-0.5rem',
            padding: '0.5rem',
            color: 'gray',
            borderRadius: '0.25rem',
          }}
        >
          Stripe Coupon Id
        </Form.Label>
        <Form.Control
          style={{ borderColor: '#fff' }}
          type="text"
          value={couponId}
          onChange={handleCouponChange}
          placeholder="N/A"
        />
      </Form.Group>
    </div>
  )
}

export const TrialCheckBox = ({
  hasTrial,
  setHasTrial,
}: {
  hasTrial: boolean
  setHasTrial: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  return (
    <div className="form-check mt-2">
      <input
        className="form-check-input"
        type="checkbox"
        checked={hasTrial}
        onChange={(e) => setHasTrial(e.target.checked)}
        id="trialCheck"
      />
      <label className="form-check-label" htmlFor="trialCheck">
        Has Trial?
      </label>
    </div>
  )
}

export const ExpiredInDaysInput = ({
  expiredInDays,
  setExpiredInDays,
}: {
  expiredInDays: string | number
  setExpiredInDays: (value: string | number) => void
}) => {
  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percent = Number(e.target.value)

    if (Number.isNaN(percent) || percent < 0) {
      alert('Invalid number of days! Please enter a value greater than 0.')
      return false
    }

    setExpiredInDays(percent)
  }
  return (
    <div className="form-floating mt-2 mb-2">
      <Form.Group className="mb-3">
        <Form.Label
          style={{
            background: '#fff',
            width: '100%',
            marginBottom: '-0.5rem',
            padding: '0.5rem',
            color: 'gray',
            borderRadius: '0.25rem',
          }}
        >
          Expired In Days
        </Form.Label>
        <Form.Control
          style={{ borderColor: '#fff' }}
          type="number"
          value={expiredInDays}
          onChange={handleChangeQuantity}
          placeholder="30"
        />
      </Form.Group>
    </div>
  )
}

export const HubspotDealLink = ({
  hubspotDealLink,
  setHubspotDealLink,
  onValidate,
  isValidating,
  validationResult,
  onInputChange,
  disabled = false,
  dealName,
}: {
  hubspotDealLink: string
  setHubspotDealLink: (value: string) => void
  onValidate?: () => void
  isValidating?: boolean
  validationResult?: {
    isValid: boolean
    error?: string
    message?: string
  } | null
  onInputChange?: () => void
  disabled?: boolean
  dealName?: string
}) => {
  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setHubspotDealLink(value)
    if (onInputChange) {
      onInputChange()
    }
  }

  if (dealName) {
    return (
      <Card className="mt-2 mb-2 text-white bg-dark">
        <Card.Body>
          <h2
            style={{ fontSize: '18px', fontWeight: 600, marginBottom: '1rem' }}
          >
            Deal
          </h2>
          <div className="d-flex align-items-center gap-2">
            <img
              src="/Hubspot_logo.png"
              alt="HubSpot"
              style={{ width: '24px', height: '24px' }}
            />
            <span className="text-white fw-semibold fs-5">{dealName}</span>
          </div>
          {validationResult &&
            !validationResult.isValid &&
            validationResult.error && (
              <div className="text-danger mt-2">
                <small>{validationResult.error}</small>
              </div>
            )}
          {validationResult && validationResult.isValid && (
            <div
              className={
                validationResult.message
                  ? 'text-warning mt-2'
                  : 'text-success mt-2'
              }
            >
              <small>
                {validationResult.message || 'Deal validated successfully!'}
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <div className="mt-2 mb-2">
        <div className="form-floating" style={{ display: 'flex' }}>
          <input
            onChange={handleChangeValue}
            className="form-control hubspot-input"
            id="hubspotDealLink"
            value={hubspotDealLink}
            placeholder="Enter deal link"
            disabled={disabled}
          />
          <label htmlFor="hubspotDealLink"> Enter Hubspot Deal Link</label>
          {onValidate && (
            <Button
              style={{ marginLeft: '8px' }}
              onClick={onValidate}
              disabled={isValidating || !hubspotDealLink.trim()}
            >
              {isValidating ? 'Checking...' : 'Check Deal'}
            </Button>
          )}
        </div>
        {validationResult &&
          !validationResult.isValid &&
          validationResult.error && (
            <div className="text-danger mt-1">
              <small>{validationResult.error}</small>
            </div>
          )}
        {validationResult && validationResult.isValid && (
          <div
            className={
              validationResult.message
                ? 'text-warning mt-1'
                : 'text-success mt-1'
            }
          >
            <small>
              {validationResult.message || 'Deal validated successfully!'}
            </small>
          </div>
        )}
      </div>
    </>
  )
}

export const CollectShippingAddress = ({
  collectShippingAddress,
  setCollectShippingAddress,
}: {
  collectShippingAddress: boolean
  setCollectShippingAddress: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  return (
    <div className="form-check mt-2">
      <input
        className="form-check-input"
        type="checkbox"
        checked={collectShippingAddress}
        onChange={(e) => setCollectShippingAddress(e.target.checked)}
        id="collectShippingAddressCheck"
      />
      <label className="form-check-label" htmlFor="collectShippingAddressCheck">
        Collect Shipping Address?
      </label>
    </div>
  )
}

export const AllowCustomerUseReferralCode = ({
  allowCustomerUseReferralCode,
  setAllowCustomerUseReferralCode,
}: {
  allowCustomerUseReferralCode: boolean
  setAllowCustomerUseReferralCode: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  return (
    <div className="form-check mt-2">
      <input
        className="form-check-input"
        type="checkbox"
        checked={allowCustomerUseReferralCode}
        onChange={(e) => setAllowCustomerUseReferralCode(e.target.checked)}
        id="allowCustomerUseReferralCodeCheck"
      />
      <label
        className="form-check-label"
        htmlFor="allowCustomerUseReferralCodeCheck"
      >
        Allow Customer to Use Referral Code?
      </label>
    </div>
  )
}
